import React, { useEffect, useRef, useState } from 'react';
import Header from './Header';
import Footer from './Footer';
import CustomCursor from './CustomCursor';

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Outfit:wght@300;400;500;600;700&display=swap');

  .icharge-root {
    font-family: 'Outfit', sans-serif;
    --green:        #00A86B;
    --green-light:  #00D68F;
    --green-dark:   #006B44;
    --green-deep:   #003822;
    --green-pale:   #B8F0DA;
    --teal:         #0097A7;
    --teal-light:   #00C8D8;
    --teal-dark:    #005F6B;
    --bg:           #F0FBF7;
    background: var(--bg);
  }
  .icharge-root *, .icharge-root *::before, .icharge-root *::after {
    box-sizing: border-box;
    font-family: 'Outfit', sans-serif;
  }
  .icharge-root h1, .icharge-root h2, .icharge-root h3,
  .icharge-root h4, .icharge-root h5, .icharge-root h6 {
    font-family: 'Cormorant Garamond', serif;
  }

  /* ── HERO ── */
  .icharge-hero {
    position: relative; overflow: hidden;
    background: linear-gradient(90deg, #608d18, #236e48, #146b91);
    min-height: 88vh; display: flex; align-items: center; justify-content: center;
  }

  .icharge-grid { position: absolute; inset: 0; pointer-events: none; perspective: 600px; }
  .icharge-grid-inner {
    position: absolute; bottom: -40px; left: -20%; right: -20%; height: 60%;
    background-image:
      linear-gradient(rgba(255,255,255,0.07) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.07) 1px, transparent 1px);
    background-size: 60px 60px;
    transform: rotateX(70deg); transform-origin: bottom center;
    animation: icharge-grid-scroll 8s linear infinite;
    mask-image: linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 80%);
    -webkit-mask-image: linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 80%);
  }
  @keyframes icharge-grid-scroll { from { background-position: 0 0; } to { background-position: 0 60px; } }

  .icharge-orbit {
    position: absolute; inset: -48px; border-radius: 50%;
    border: 1px solid rgba(255,255,255,0.2);
    animation: icharge-orbit3d 8s linear infinite; transform-style: preserve-3d;
  }
  .icharge-orbit-2 { inset: -72px; border-color: rgba(0,214,143,0.22); animation: icharge-orbit3d 12s linear infinite reverse; }
  @keyframes icharge-orbit3d {
    0%   { transform: rotateX(70deg) rotateZ(0deg); }
    100% { transform: rotateX(70deg) rotateZ(360deg); }
  }
  .icharge-orbit::after {
    content: ''; position: absolute; top: -4px; left: 50%;
    width: 8px; height: 8px; background: #fff; border-radius: 50%;
    box-shadow: 0 0 12px 3px rgba(255,255,255,0.9); transform: translateX(-50%);
  }
  .icharge-orbit-2::after { background: var(--green-pale); box-shadow: 0 0 12px 3px rgba(184,240,218,0.85); top: auto; bottom: -4px; }

  .icharge-particle { position: absolute; border-radius: 50%; pointer-events: none; animation: icharge-particle-float linear infinite; }
  .icharge-particle:nth-child(1) { width:4px; height:4px; background:rgba(0,168,107,0.9);  left:15%; top:20%; animation-duration:7s;  animation-delay:0s; }
  .icharge-particle:nth-child(2) { width:3px; height:3px; background:rgba(0,214,143,0.8);  left:80%; top:60%; animation-duration:9s;  animation-delay:1s; }
  .icharge-particle:nth-child(3) { width:5px; height:5px; background:rgba(255,255,255,0.6); left:60%; top:15%; animation-duration:11s; animation-delay:2s; }
  .icharge-particle:nth-child(4) { width:3px; height:3px; background:rgba(184,240,218,0.7); left:30%; top:75%; animation-duration:8s;  animation-delay:3s; }
  .icharge-particle:nth-child(5) { width:4px; height:4px; background:rgba(0,151,167,0.8);  left:90%; top:30%; animation-duration:10s; animation-delay:0.5s; }
  @keyframes icharge-particle-float {
    0%   { transform: translateY(0) scale(1); opacity: 0; }
    10%  { opacity: 1; } 90% { opacity: 1; }
    100% { transform: translateY(-80px) scale(0.5); opacity: 0; }
  }

  .icharge-blob { position: absolute; border-radius: 50%; filter: blur(80px); pointer-events: none; }
  .icharge-blob-1 { width:500px; height:500px; background:rgba(0,100,60,0.45); top:-100px; right:-100px; animation:icharge-drift 10s ease-in-out infinite alternate; }
  .icharge-blob-2 { width:360px; height:360px; background:rgba(0,55,35,0.5); bottom:-80px; left:-60px; animation:icharge-drift 13s ease-in-out infinite alternate-reverse; }
  @keyframes icharge-drift { from { transform:translate(0,0) scale(1); } to { transform:translate(30px,20px) scale(1.08); } }

  .icharge-hero-content {
    position: relative; z-index: 10;
    display: flex; flex-direction: row; align-items: center;
    gap: 80px; max-width: 1200px; width: 100%;
    padding: 120px 60px 80px 80px;
  }
  .icharge-image-wrap { flex-shrink: 0; position: relative; }

  .icharge-image-glow {
    position: absolute; inset: -24px;
    background: radial-gradient(circle, rgba(0,168,107,0.45) 0%, transparent 70%);
    border-radius: 50%; animation: icharge-glow-pulse 3s ease-in-out infinite;
  }
  @keyframes icharge-glow-pulse { 0%,100%{opacity:0.5;transform:scale(1);} 50%{opacity:1;transform:scale(1.06);} }

  .icharge-image-3d {
    transform-style: preserve-3d; transition: transform 0.1s ease;
    cursor: pointer; position: relative;
  }
  .icharge-image-3d::before {
    content: ''; position: absolute; inset: -2px; border-radius: 26px;
    background: linear-gradient(135deg, #fff, var(--green-pale), var(--green-light));
    z-index: 1; opacity: 0; transition: opacity 0.3s ease;
  }
  .icharge-image-3d:hover::before { opacity: 1; }
  .icharge-image-3d::after {
    content: ''; position: absolute; inset: 0; border-radius: 24px;
    background: linear-gradient(135deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.18) 50%, rgba(255,255,255,0) 100%);
    background-size: 200% 200%; background-position: 200% 200%;
    z-index: 3; pointer-events: none; opacity: 0;
    transition: opacity 0.3s ease, background-position 0.4s ease;
  }
  .icharge-image-3d:hover::after { opacity: 1; background-position: -20% -20%; }

  .icharge-image {
    width: 380px; height: 380px; object-fit: cover; border-radius: 24px;
    position: relative; z-index: 2;
    box-shadow: 0 30px 80px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.2), 20px 20px 60px rgba(0,0,0,0.2);
    transition: box-shadow 0.3s ease;
  }
  .icharge-image-3d:hover .icharge-image {
    box-shadow: 0 50px 100px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.5),
      -20px 20px 60px rgba(0,107,68,0.35), 20px -10px 40px rgba(0,214,143,0.3);
  }

  .icharge-image-reflection {
    position: absolute; bottom: -20px; left: 10%; right: 10%; height: 30px;
    background: radial-gradient(ellipse, rgba(0,168,107,0.4) 0%, transparent 70%);
    filter: blur(8px); animation: icharge-reflection-pulse 3s ease-in-out infinite;
  }
  @keyframes icharge-reflection-pulse { 0%,100%{opacity:0.5;transform:scaleX(0.9);} 50%{opacity:0.9;transform:scaleX(1.05);} }

  .icharge-hero-text { flex: 1; padding-left: 40px; }

  .icharge-eyebrow {
    font-family: 'Outfit', sans-serif;
    font-size: 11px; font-weight: 600; letter-spacing: 0.25em; text-transform: uppercase;
    color: rgba(255,255,255,0.85); margin-bottom: 16px;
    opacity: 0; animation: icharge-slideUp 0.8s ease forwards 0.3s;
  }
  .icharge-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(54px,7vw,82px); font-weight: 600; line-height: 1;
    color: white; margin: 0 0 8px;
    opacity: 0; animation: icharge-slideUp 0.8s ease forwards 0.5s;
  }
  .icharge-title-accent {
    display: block; font-family: 'Outfit', sans-serif;
    font-size: clamp(14px,1.8vw,18px); font-weight: 400;
    background: linear-gradient(90deg, #D4F7EA, #B8F0DA);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
    margin-top: 6px; letter-spacing: 0.08em; text-transform: uppercase;
  }
  .icharge-divider {
    width: 60px; height: 2px;
    background: linear-gradient(90deg, #fff, rgba(255,255,255,0.3));
    margin: 24px 0;
    opacity: 0; animation: icharge-slideUp 0.8s ease forwards 0.7s;
  }
  .icharge-desc {
    font-family: 'Outfit', sans-serif;
    font-size: 17px; line-height: 1.78; font-weight: 300;
    color: rgba(255,255,255,0.85); max-width: 480px;
    opacity: 0; animation: icharge-slideUp 0.8s ease forwards 0.9s;
  }

  .icharge-back-btn-wrap {
    margin-top: 48px;
    opacity: 0; animation: icharge-slideUp 0.8s ease forwards 1.1s;
  }
  .icharge-back-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 10px 22px; border-radius: 100px;
    background: rgba(255,255,255,0.07);
    border: 1px solid rgba(255,255,255,0.3);
    color: rgba(255,255,255,0.75);
    font-family: 'Outfit', sans-serif;
    font-size: 0.78rem; font-weight: 500;
    letter-spacing: 0.08em; text-transform: uppercase;
    text-decoration: none;
    transition: background 0.3s, border-color 0.3s, color 0.3s, transform 0.3s;
  }
  .icharge-back-btn:hover {
    background: rgba(0,168,107,0.18);
    border-color: rgba(0,214,143,0.6);
    color: var(--green-pale);
    transform: translateX(-4px);
  }
  .icharge-site-link-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 10px 22px; border-radius: 100px;
    background: rgba(255,255,255,0.07);
    border: 1px solid rgba(184,240,218,0.45);
    color: var(--green-pale);
    font-family: 'Outfit', sans-serif;
    font-size: 0.78rem; font-weight: 500;
    letter-spacing: 0.08em; text-transform: uppercase;
    text-decoration: none;
    transition: background 0.3s, border-color 0.3s, color 0.3s, transform 0.3s;
  }
  .icharge-site-link-btn:hover {
    background: rgba(184,240,218,0.18);
    border-color: var(--green-pale);
    color: #fff;
    transform: translateX(-4px);
  }

  @keyframes icharge-slideUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }

  /* ── iCHARGE IN ACTION ── */
  .icharge-action-section {
    background: #06291a;
    padding: 100px 40px;
    position: relative; overflow: hidden;
  }
  .icharge-action-section::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(0,168,107,0.6), transparent);
  }
  .icharge-action-section::after {
    content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(184,240,218,0.3), transparent);
  }

  .icharge-action-inner {
    max-width: 1100px; margin: 0 auto;
    display: flex; flex-direction: row; align-items: center;
    gap: 80px;
  }

  .icharge-action-text { flex: 1; min-width: 0; }
  .icharge-action-eyebrow {
    font-family: 'Outfit', sans-serif; font-size: 0.6rem; font-weight: 500;
    letter-spacing: 0.3em; text-transform: uppercase; color: var(--green-pale);
    display: flex; align-items: center; gap: 12px; margin-bottom: 20px;
  }
  .icharge-action-eyebrow-line { display: inline-block; width: 32px; height: 1px; background: var(--green-light); }

  .icharge-action-heading {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(36px,5vw,60px); font-weight: 500; line-height: 1.1;
    color: white; margin: 0 0 8px;
  }
  .icharge-action-heading-italic {
    font-family: 'Cormorant Garamond', serif;
    font-style: italic; font-weight: 400;
    color: var(--green-pale); display: block; margin-top: 4px;
  }

  .icharge-action-divider {
    width: 48px; height: 1px;
    background: linear-gradient(90deg, var(--green-light), transparent);
    margin: 24px 0;
  }

  .icharge-action-desc {
    font-family: 'Outfit', sans-serif; font-weight: 300;
    font-size: 16px; line-height: 1.8; color: rgba(255,255,255,0.65);
    max-width: 420px; margin-bottom: 32px;
  }

  .icharge-action-yt-link {
    display: inline-flex; align-items: center; gap: 10px;
    padding: 12px 26px; border-radius: 100px;
    background: rgba(0,168,107,0.15);
    border: 1px solid rgba(0,168,107,0.45);
    color: var(--green-pale);
    font-family: 'Outfit', sans-serif; font-size: 0.78rem;
    font-weight: 500; letter-spacing: 0.08em; text-transform: uppercase;
    text-decoration: none;
    transition: background 0.3s, border-color 0.3s, transform 0.3s, box-shadow 0.3s;
  }
  .icharge-action-yt-link:hover {
    background: rgba(0,168,107,0.3);
    border-color: var(--green-pale);
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(0,56,34,0.5);
  }
  .icharge-action-yt-icon {
    width: 28px; height: 28px; background: #FF0000; border-radius: 6px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }

  .icharge-action-video-col { flex-shrink: 0; width: 460px; }

  .icharge-action-video-card {
    background: rgba(255,255,255,0.04); border: 1px solid rgba(0,168,107,0.28);
    border-radius: 16px; width: 100%; cursor: pointer;
    transition: background 0.3s, border-color 0.3s, box-shadow 0.3s, transform 0.3s;
    overflow: hidden;
  }
  .icharge-action-video-card:hover {
    background: rgba(0,168,107,0.12); border-color: rgba(0,168,107,0.6);
    transform: translateY(-6px) scale(1.02); box-shadow: 0 20px 48px rgba(0,56,34,0.45);
  }

  .icharge-action-video-thumb {
    position: relative; width: 100%; aspect-ratio: 16/9; overflow: hidden;
    background: rgba(0,56,34,0.4); display: flex; align-items: center; justify-content: center;
  }
  .icharge-action-video-thumb img {
    position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; opacity: 0.85;
    transition: transform 0.4s ease;
  }
  .icharge-action-video-card:hover .icharge-action-video-thumb img { transform: scale(1.06); }

  .icharge-action-play-overlay {
    position: absolute; inset: 0; background: rgba(0,26,15,0.45);
    display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 10px;
    transition: background 0.3s;
  }
  .icharge-action-video-card:hover .icharge-action-play-overlay { background: rgba(0,56,34,0.6); }

  .icharge-action-play-btn {
    width: 56px; height: 56px; background: rgba(255,255,255,0.95); border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 20px; padding-left: 4px; color: var(--green);
    box-shadow: 0 4px 20px rgba(0,56,34,0.5);
    transition: transform 0.3s, box-shadow 0.3s;
  }
  .icharge-action-video-card:hover .icharge-action-play-btn { transform: scale(1.15); box-shadow: 0 8px 30px rgba(0,168,107,0.6); }

  .icharge-action-yt-badge {
    background: #FF0000; color: white; font-family: 'Outfit', sans-serif;
    font-size: 9px; font-weight: 700; letter-spacing: 0.08em;
    padding: 3px 8px; border-radius: 4px; text-transform: uppercase;
  }

  .icharge-action-card-body { padding: 14px 18px 16px; text-align: left; }
  .icharge-action-card-name { font-family: 'Outfit', sans-serif; font-weight: 600; font-size: 15px; color: white; margin-bottom: 3px; }
  .icharge-action-card-topic { font-family: 'Outfit', sans-serif; font-size: 11px; color: rgba(184,240,218,0.85); font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; line-height: 1.4; }
  .icharge-action-card-watch { font-family: 'Outfit', sans-serif; font-size: 11px; color: rgba(255,255,255,0.4); margin-top: 5px; display: flex; align-items: center; gap: 4px; }

  /* ── VIDEO MODAL ── */
  .icharge-modal-backdrop {
    position: fixed; inset: 0; background: rgba(0,12,6,0.92); z-index: 1000;
    display: flex; align-items: center; justify-content: center; padding: 24px;
    animation: icharge-modal-fade 0.25s ease;
  }
  @keyframes icharge-modal-fade { from{opacity:0;} to{opacity:1;} }
  .icharge-modal-box {
    position: relative; width: 100%; max-width: 780px;
    background: #001A0F; border-radius: 24px;
    border: 1px solid rgba(0,214,143,0.3);
    box-shadow: 0 40px 120px rgba(0,0,0,0.85), 0 0 0 1px rgba(0,168,107,0.15);
    overflow: hidden; animation: icharge-modal-up 0.3s cubic-bezier(0.34,1.56,0.64,1);
  }
  @keyframes icharge-modal-up { from{opacity:0;transform:scale(0.88) translateY(32px);} to{opacity:1;transform:scale(1) translateY(0);} }
  .icharge-modal-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 18px 20px 14px; border-bottom: 1px solid rgba(0,214,143,0.15);
  }
  .icharge-modal-title { font-family: 'Outfit', sans-serif; font-weight: 600; font-size: 15px; color: white; display: flex; align-items: center; gap: 8px; }
  .icharge-modal-badge {
    background: linear-gradient(135deg, var(--green-dark), var(--green-light));
    color: white; font-family: 'Outfit', sans-serif; font-size: 10px; font-weight: 600;
    letter-spacing: 0.1em; text-transform: uppercase; padding: 3px 10px; border-radius: 100px;
  }
  .icharge-modal-close {
    width: 32px; height: 32px; background: rgba(255,255,255,0.08); border: none;
    border-radius: 50%; color: rgba(255,255,255,0.7); font-size: 18px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: background 0.2s, color 0.2s; flex-shrink: 0;
  }
  .icharge-modal-close:hover { background: rgba(239,68,68,0.3); color: white; }
  .icharge-modal-frame { position: relative; width: 100%; aspect-ratio: 16/9; background: #000; }
  .icharge-modal-frame iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: none; }
  .icharge-modal-footer { padding: 14px 20px; display: flex; align-items: center; justify-content: space-between; gap: 10px; }
  .icharge-modal-yt-link {
    display: flex; align-items: center; gap: 6px;
    font-family: 'Outfit', sans-serif; font-size: 12px; font-weight: 600;
    color: rgba(255,255,255,0.5); text-decoration: none; transition: color 0.2s;
  }
  .icharge-modal-yt-link:hover { color: #FF4444; }
  .icharge-modal-topic { font-family: 'Outfit', sans-serif; font-size: 11px; color: rgba(184,240,218,0.7); letter-spacing: 0.06em; text-transform: uppercase; }

  /* ── RESPONSIVE ── */

  @media (max-width: 1024px) {
    .icharge-hero-content { gap: 48px; padding: 120px 40px 80px 48px; }
    .icharge-image { width: 320px; height: 320px; }
    .icharge-action-video-col { width: 380px; }
  }

  @media (max-width: 900px) {
    .icharge-action-section { padding: 80px 32px; }
    .icharge-action-inner { flex-direction: column; gap: 48px; align-items: center; text-align: center; }
    .icharge-action-eyebrow { justify-content: center; }
    .icharge-action-desc { margin: 0 auto 32px; max-width: 100%; }
    .icharge-action-yt-link { margin: 0 auto; }
    .icharge-action-video-col { width: 100%; max-width: 520px; }
    .icharge-action-divider { margin: 24px auto; }
  }

  @media (max-width: 768px) {
    .icharge-hero { min-height: auto; }
    .icharge-hero-content {
      flex-direction: column; gap: 40px;
      padding: 100px 24px 60px; text-align: center;
    }
    .icharge-hero-text { padding-left: 0; }
    .icharge-image { width: 280px; height: 280px; }
    .icharge-divider { margin: 24px auto; }
    .icharge-desc { margin: 0 auto; font-size: 15px; }
    .icharge-eyebrow { text-align: center; }
    .icharge-back-btn-wrap { justify-content: center; }
    .icharge-action-section { padding: 64px 20px; }
    .icharge-action-heading { font-size: clamp(30px,8vw,48px); }
    .icharge-action-desc { font-size: 15px; }
    .icharge-modal-box { border-radius: 16px; }
    .icharge-modal-header { padding: 14px 16px 12px; }
    .icharge-modal-footer { padding: 10px 16px; flex-wrap: wrap; gap: 8px; }
    .icharge-modal-topic { font-size: 10px; }
  }

  @media (max-width: 480px) {
    .icharge-hero-content { padding: 90px 16px 48px; gap: 32px; }
    .icharge-image { width: 240px; height: 240px; border-radius: 18px; }
    .icharge-title { font-size: clamp(44px,12vw,64px); }
    .icharge-title-accent { font-size: 13px; }
    .icharge-desc { font-size: 14px; }
    .icharge-back-btn, .icharge-site-link-btn { font-size: 0.7rem; padding: 9px 16px; }
    .icharge-action-section { padding: 48px 16px; }
    .icharge-action-video-col { max-width: 100%; }
    .icharge-action-yt-link { padding: 10px 18px; font-size: 0.72rem; }
    .icharge-modal-backdrop { padding: 12px; }
    .icharge-modal-box { border-radius: 12px; }
    .icharge-modal-title { font-size: 13px; }
    .icharge-modal-badge { font-size: 9px; padding: 2px 8px; }
    .icharge-modal-close { width: 28px; height: 28px; font-size: 15px; }
  }
`;

const ICHARGE_VIDEO_ID = 'A_Cz6hoWcwk';

function AnimatedSection({ children }) {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} style={{ opacity:visible?1:0, transform:visible?'none':'translateY(28px)', transition:'opacity 0.7s ease, transform 0.7s ease' }}>
      {children}
    </div>
  );
}

const ICharge = () => {
  const [activeVideo, setActiveVideo] = useState(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setActiveVideo(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleMouseMove = (e) => {
    const el = imageRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left, y = e.clientY - rect.top;
    const cx = rect.width / 2, cy = rect.height / 2;
    el.style.transform = `perspective(800px) rotateX(${-((y-cy)/cy)*18}deg) rotateY(${((x-cx)/cx)*18}deg) scale3d(1.06,1.06,1.06)`;
  };
  const handleMouseLeave = () => {
    if (imageRef.current) imageRef.current.style.transform = `perspective(800px) rotateX(0deg) rotateY(0deg) scale3d(1,1,1)`;
  };

  return (
    <>
      <CustomCursor />
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <div className="icharge-root min-h-screen flex flex-col">
        <Header />

        {/* ── Hero ── */}
        <section className="icharge-hero">
          <div className="icharge-grid"><div className="icharge-grid-inner" /></div>
          <div className="icharge-particle" /><div className="icharge-particle" /><div className="icharge-particle" />
          <div className="icharge-particle" /><div className="icharge-particle" />
          <div className="icharge-blob icharge-blob-1" />
          <div className="icharge-blob icharge-blob-2" />

          <div className="icharge-hero-content">
            <div className="icharge-image-wrap">
              <div className="icharge-image-glow" />
              <div className="icharge-orbit" />
              <div className="icharge-orbit icharge-orbit-2" />
              <div className="icharge-image-3d" ref={imageRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
                <img src="/assets/product_icharge.jpg" alt="iCharge" className="icharge-image" />
              </div>
              <div className="icharge-image-reflection" />
            </div>

            <div className="icharge-hero-text">
              <p className="icharge-eyebrow">Energy & Vitality Formula</p>
              <h1 className="icharge-title">
                iCharge
                <span className="icharge-title-accent">Product Description</span>
              </h1>
              <div className="icharge-divider" />
              <p className="icharge-desc">
                iCharge is a scientifically advanced energy formulation designed to fuel your body and mind at peak performance. Engineered for sustained vitality, mental clarity, and physical endurance — so you can power through every day with unstoppable momentum.
              </p>
              <div className="icharge-back-btn-wrap" style={{ display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap' }}>
                <a href="/products" className="icharge-back-btn">← Back to Products</a>
                 <a href="https://vivaicharge.com/" target="_blank" rel="noopener noreferrer" className="icharge-site-link-btn">
                  Visit iCharge Site ↗
                </a> 
              </div>
            </div>
          </div>
        </section>

        {/* ── iCharge in Action ── */}
        <section className="icharge-action-section">
          <div className="icharge-action-inner">

            {/* Left: text */}
            <AnimatedSection>
              <div className="icharge-action-text">
                <p className="icharge-action-eyebrow">
                  <span className="icharge-action-eyebrow-line" />
                  YouTube · Watch Now
                </p>
                <h2 className="icharge-action-heading">
                  iCharge
                  <span className="icharge-action-heading-italic">in Action</span>
                </h2>
                <div className="icharge-action-divider" />
                <p className="icharge-action-desc">
                  Watch iCharge in action — discover how our advanced energy formulation delivers sustained vitality, sharp mental focus, and peak physical performance to help you conquer every challenge.
                </p>
                <a
                  href={`https://www.youtube.com/watch?v=${ICHARGE_VIDEO_ID}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="icharge-action-yt-link"
                >
                  <span className="icharge-action-yt-icon">
                    <svg width="14" height="10" viewBox="0 0 14 10" fill="white">
                      <path d="M5.5 7.5v-5l4.5 2.5-4.5 2.5z"/>
                    </svg>
                  </span>
                  Watch on YouTube
                </a>
              </div>
            </AnimatedSection>

            {/* Right: video card */}
            <AnimatedSection>
              <div className="icharge-action-video-col">
                <div
                  className="icharge-action-video-card"
                  onClick={() => setActiveVideo({ youtubeId: ICHARGE_VIDEO_ID, lang: "iCharge in Action", topic: "Energy · Vitality · Mental Clarity · Peak Performance" })}
                >
                  <div className="icharge-action-video-thumb">
                    <img
                      src={`https://i.ytimg.com/vi/${ICHARGE_VIDEO_ID}/hqdefault.jpg`}
                      alt="iCharge in Action"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="icharge-action-play-overlay">
                      <div className="icharge-action-play-btn">▶</div>
                      <span className="icharge-action-yt-badge">YouTube</span>
                    </div>
                  </div>
                  <div className="icharge-action-card-body">
                    <div className="icharge-action-card-name">iCharge — Energy & Vitality</div>
                    <div className="icharge-action-card-topic">Energy · Vitality · Mental Clarity · Peak Performance</div>
                    <div className="icharge-action-card-watch"><span>▶</span> Watch Video</div>
                  </div>
                </div>
              </div>
            </AnimatedSection>

          </div>
        </section>

        {/* ── Video Modal ── */}
        {activeVideo && (
          <div className="icharge-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setActiveVideo(null); }}>
            <div className="icharge-modal-box">
              <div className="icharge-modal-header">
                <div className="icharge-modal-title">
                  <span>iCharge</span>
                  <span className="icharge-modal-badge">{activeVideo.lang}</span>
                </div>
                <button className="icharge-modal-close" onClick={() => setActiveVideo(null)}>✕</button>
              </div>
              <div className="icharge-modal-frame">
                <iframe
                  src={`https://www.youtube.com/embed/${activeVideo.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
                  title={`iCharge — ${activeVideo.lang}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="icharge-modal-footer">
                <span className="icharge-modal-topic">{activeVideo.topic}</span>
                <a href={`https://www.youtube.com/watch?v=${activeVideo.youtubeId}`} target="_blank" rel="noopener noreferrer" className="icharge-modal-yt-link">
                  <span style={{ fontSize:'14px' }}>▶</span> Open on YouTube
                </a>
              </div>
            </div>
          </div>
        )}

        <Footer />
      </div>
    </>
  );
};

export default ICharge;